import React, { useRef, useState } from "react";
import { Download, Upload, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { getToken } from "../../adminApi";
import { API_BASE } from "../../apiBase";
import { Card, SectionTitle, Btn } from "../../components/admin/AdminUI";

const BASE = API_BASE;

export default function AdminBackup() {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const input = useRef(null);

  const exportBackup = async () => {
    setBusy("export");
    setMsg("");
    setErr("");
    try {
      const res = await fetch(`${BASE}/admin/backup`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("Yedek alınamadı");
      const blob = await res.blob();
      const stamp = new Date().toISOString().slice(0, 16).replace("T", "-").replace(":", "");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `hrd-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      setMsg("Yedek dosyası indirildi.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  };

  const restore = async (file) => {
    if (!file) return;
    if (
      !window.confirm(
        "Bu işlem yedekteki içeriği geri yükler ve mevcut kayıtların üzerine yazar. Devam etmek istiyor musunuz?"
      )
    )
      return;
    setBusy("import");
    setMsg("");
    setErr("");
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await fetch(`${BASE}/admin/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Geri yükleme başarısız");
      setMsg(
        `Geri yükleme tamamlandı: ${Object.entries(data.restored)
          .map(([k, v]) => `${k} (${v})`)
          .join(", ")}. Siteyi yenileyin.`
      );
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div data-testid="admin-backup">
      <SectionTitle label="[ AYARLAR ]" title="Yedekleme ve geri yükleme" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-7 lg:p-9">
          <ShieldCheck size={20} strokeWidth={1.4} className="text-[#8C7862] mb-7" />
          <h2 className="display text-[24px] text-[#141414] leading-tight">Yedek al</h2>
          <p className="text-[14px] text-[#141414]/70 mt-4 leading-relaxed">
            Tüm site içeriği, projeler, koleksiyonlar, ekip, mesajlar ve görsel kayıtları tek bir JSON
            dosyası olarak indirilir. Görsel ve PDF dosyalarının kendisi kalıcı bulut depolamada durur,
            yedek bu dosyaların adreslerini içerir.
          </p>
          <Btn onClick={exportBackup} disabled={busy === "export"} data-testid="backup-export" className="mt-8">
            {busy === "export" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} strokeWidth={1.7} />}
            {busy === "export" ? "HAZIRLANIYOR..." : "YEDEĞİ İNDİR (JSON)"}
          </Btn>
        </Card>

        <Card className="p-7 lg:p-9">
          <AlertTriangle size={20} strokeWidth={1.4} className="text-[#8C7862] mb-7" />
          <h2 className="display text-[24px] text-[#141414] leading-tight">Yedeği geri yükle</h2>
          <p className="text-[14px] text-[#141414]/70 mt-4 leading-relaxed">
            Daha önce indirdiğiniz JSON dosyasını yükleyin. Yedekteki bölümler tek tıkla eski haline
            döner ve mevcut kayıtların üzerine yazılır. Bu işlem geri alınamaz.
          </p>
          <Btn
            variant="ghost"
            onClick={() => input.current?.click()}
            disabled={busy === "import"}
            data-testid="backup-import"
            className="mt-8"
          >
            {busy === "import" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={1.7} />}
            {busy === "import" ? "GERİ YÜKLENİYOR..." : "YEDEK DOSYASI SEÇ"}
          </Btn>
          <input
            ref={input}
            type="file"
            accept="application/json,.json"
            className="hidden"
            data-testid="backup-input"
            onChange={(e) => restore(e.target.files?.[0])}
          />
        </Card>
      </div>

      {msg && (
        <p className="mono text-[9px] text-[#8C7862] mt-6" data-testid="backup-message">
          {msg}
        </p>
      )}
      {err && (
        <p className="text-[13px] text-[#8C2E1D] mt-6 border border-[#D9B7AE] bg-[#FBF1EE] px-4 py-3">{err}</p>
      )}

      <Card className="p-7 mt-4">
        <p className="mono text-[9px] text-[#6B675F] mb-4">KALICI SAKLAMA</p>
        <ul className="space-y-2.5 text-[13.5px] text-[#141414]/70">
          <li>Paneldeki her kayıt anında MongoDB veritabanına yazılır (hrd_mimarlik).</li>
          <li>Yüklenen görsel ve PDF'ler kalıcı bulut depolamada tutulur, yeniden yayınlamada silinmez.</li>
          <li>Sayfa yenileme, tarayıcı önbelleği temizleme ve yeni deploy içeriği etkilemez.</li>
          <li>Her büyük değişiklikten önce yedek almanız önerilir.</li>
        </ul>
      </Card>
    </div>
  );
}
